/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
 */

import { __ } from '@oat-sa-private/ui-core';
import jwtTokenHandlerFactory from 'core/jwt/jwtTokenHandler';
import config from '@/config';
import fetchRequest from 'core/fetchRequest';
import xmlHttpRequest from 'core/xmlHttpRequest';
import router from '@/core/router';
import { compile } from 'path-to-regexp';

export const getEndpointUrl = (endpointName, variables = {}, parameters = {}, baseUrl = null) => {
    // get endpoint
    let endpoint = config.endpoints[endpointName];

    for (let variable in variables) {
        if (variables[variable]) {
            variables[variable] = encodeURIComponent(variables[variable]);
        }
    }

    // replace variables
    endpoint = compile(endpoint)(variables);

    const base = baseUrl && baseUrl !== 'undefined' ? baseUrl : config.baseUrls.api;
    const url = new URL(endpoint, base);
    const urlParams = new URLSearchParams(parameters);
    let finalUrl = (base + url.pathname).replace(/([^:]\/)\/+/g, '$1');

    if (urlParams.toString() !== '') {
        finalUrl += `?${urlParams.toString()}`;
    }

    return finalUrl;
};

const refreshTokenParameters = {
    grant_type: 'refresh_token'
};

export const jwtTokenHandler = jwtTokenHandlerFactory({
    refreshTokenUrl: getEndpointUrl('refreshToken', {}, {}, config.baseUrls.authServer),
    oauth2RequestFormat: true,
    refreshTokenParameters
});

/**
 * Redirect to login page if the user is not authenticated
 */
const handleTokenExpiration = () => {
    const historyState = {
        backPath: window.location.pathname + window.location.search,
        url: config.routes.login.main
    };
    router.replace(historyState);
};

/**
 * Redirect to error page if url is not valid
 */
const handleInvalidUrl = () => {
    router.redirect(config.routes.error);
};

/**
 * Read the error sent by the API and forward it
 * @param {Response} response
 * @returns {Promise<void>}
 */
const handleApiError = response =>
    response.json().then(res => {
        res.responseStatus = response.status;
        const translation = res.translation || (res.error && res.error.translation);

        if (translation) {
            const { key, params } = translation;
            const message = __(key, ...params);

            if (res.error.translation) {
                res.error.message = message;
            } else {
                res.message = message;
            }
        }

        throw res;
    });

export default (url, options, noNotFoundRedirect) => {
    const httpRequestMethod = options?.httpRequestImplementation === 'xhr' ? xmlHttpRequest : fetchRequest;
    let triedTokenRefresh = false;
    const performApiCall = () =>
        httpRequestMethod(url, { timeout: 30000, ...options, jwtTokenHandler }).catch(error => {
            const response = error.response;

            const { message } = error;

            if (
                response?.status === 401 ||
                message === 'Token not available and cannot be refreshed' ||
                message === 'Refresh token is not available'
            ) {
                handleTokenExpiration();
            } else if (response?.status === 403) {
                if (triedTokenRefresh) {
                    return handleApiError(response);
                } else {
                    //retry the api call after the token refresh
                    return jwtTokenHandler.refreshToken().then(() => {
                        triedTokenRefresh = true;
                        return performApiCall();
                    });
                }
            } else if (response?.status === 404 && !noNotFoundRedirect) {
                handleInvalidUrl();
            } else if (response) {
                return handleApiError(response);
            } else {
                throw error;
            }
        });
    return performApiCall();
};
